import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import Skeleton, { CardSkeleton } from '../../Components/common/Skeleton';
import { ClipboardCheck, MapPin, Award, User, CreditCard, Phone, Globe, Eye, EyeOff } from 'lucide-react';
import { getAvatarUrl } from '../../utils/avatar';
import DoctorEditModal from '../../Components/doctors/DoctorEditModal';
import DoctorWebsiteModal from '../../Components/doctors/DoctorWebsiteModal';


/**
 * Routes S3 document URLs through the backend proxy so the browser never
 * hits S3 directly (avoids private-bucket 403 / CORS issues).
 * Local/non-S3 URLs are returned as-is.
 */
const getDocumentUrl = (url) => {
  if (!url) return '';
  if (url.includes('amazonaws.com') || url.includes('s3.')) {
    return `/api/upload/proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const DoctorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [editModalTab, setEditModalTab] = useState('professional');
  const [verificationStatus, setVerificationStatus] = useState('verified');
  const [rejectionReason, setRejectionReason] = useState('');
  const [requestingAvailability, setRequestingAvailability] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [surgeries, setSurgeries] = useState([]);

  const handleToggleWebsiteVisibility = async () => {
    const nextState = doctor.showOnWebsite === false;
    setUpdatingVisibility(true);
    try {
      await api.patch(`/doctors/${id}/website-visibility`, {
        showOnWebsite: nextState
      });
      setDoctor(prev => ({
        ...prev,
        showOnWebsite: nextState
      }));
      toast.success(nextState ? 'Doctor is now visible on the website!' : 'Doctor is now hidden from the website!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update website visibility');
    } finally {
      setUpdatingVisibility(false);
    }
  };

  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'customer_care' && user.role !== 'cc_head') {
      toast.error('Access Denied: Admin, CC or CC Head role required');
      navigate('/dashboard');
      return;
    }
    fetchDoctor();
    fetchSurgeries();
  }, [id, user]);

  const fetchDoctor = async () => {
    try {
      const response = await api.get(`/doctors/${id}`);
      setDoctor(response.data.data);
    } catch (error) {
      toast.error('Failed to load doctor');
      navigate('/doctors');
    } finally {
      setLoading(false);
    }
  };

  const fetchSurgeries = async () => {
    try {
      const response = await api.get('/surgeries', { params: { doctorId: id, limit: 100 } });
      setSurgeries(response.data.data.surgeries);
    } catch (error) {
      console.error('Failed to fetch surgeries for doctor');
    }
  };

  const fetchAvailability = async () => {
    try {
      const response = await api.get(`/doctor-availability/${id}`);
      setAvailability(response.data.data);
    } catch (error) {
      console.error('Failed to load availability');
    }
  };

  useEffect(() => {
    fetchAvailability();
  }, [id]);

  const handleVerify = async () => {
    setUpdating(true);
    try {
      await api.put(`/doctors/${id}/verify`, {
        status: verificationStatus,
        rejectionReason: verificationStatus === 'rejected' ? rejectionReason : undefined
      });
      toast.success(`Doctor ${verificationStatus} successfully!`);
      setShowVerifyModal(false);
      setVerificationStatus('verified');
      setRejectionReason('');
      fetchDoctor();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to verify doctor');
    } finally {
      setUpdating(false);
    }
  };

  const handleRequestAvailability = async () => {
    setRequestingAvailability(true);
    try {
      await api.post('/doctor-availability/request', { doctorId: id });
      toast.success('Availability form sent to doctor via Email & WhatsApp!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send request');
    } finally {
      setRequestingAvailability(false);
    }
  };

  const now = new Date();
  const upcomingSurgeries = surgeries.filter(s => {
    const scheduled = new Date(s.scheduledDate);
    return scheduled >= now && s.status !== 'cancelled' && s.status !== 'completed';
  }).sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));

  const previousSurgeries = surgeries.filter(s => {
    const scheduled = new Date(s.scheduledDate);
    return scheduled < now || s.status === 'cancelled' || s.status === 'completed';
  }).sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate));

  const getVerificationColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      verified: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getDayDateObj = (dayName) => {
    let anchor = new Date();
    if (availability?.expiryDate) {
      // Find the Sunday of the week containing the expiry date
      const exp = new Date(availability.expiryDate);
      anchor = new Date(exp);
      anchor.setDate(exp.getDate() - exp.getDay());
    } else if (availability?.completedAt) {
      const comp = new Date(availability.completedAt);
      anchor = new Date(comp);
      anchor.setDate(comp.getDate() - comp.getDay());
    }

    anchor.setHours(0, 0, 0, 0);

    const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const targetDayIndex = daysOfWeek.indexOf(dayName.toLowerCase());

    if (targetDayIndex === -1) return null;

    for (let i = 0; i < 7; i++) {
      const current = new Date(anchor);
      current.setDate(anchor.getDate() + i);
      if (current.getDay() === targetDayIndex) {
        return current;
      }
    }
    return null;
  };

  const getDayDate = (dayName) => {
    const dateObj = getDayDateObj(dayName);
    if (!dateObj) return '';
    return dateObj.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-10 w-10 bg-slate-200 animate-pulse rounded-lg"></div>
            <div className="h-8 w-48 bg-slate-200 animate-pulse rounded-lg"></div>
          </div>
          <div className="h-10 w-32 bg-slate-100 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-6">
              <div className="h-6 w-1/3 bg-slate-100 rounded animate-pulse"></div>
              <div className="grid grid-cols-2 gap-8">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="space-y-2">
                    <div className="h-3 w-20 bg-slate-50 rounded animate-pulse"></div>
                    <div className="h-5 w-full bg-slate-100 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
            <CardSkeleton />
          </div>
          <div className="space-y-6">
            <CardSkeleton />
            <CardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Doctor not found</p>
        <Link to="/doctors" className="text-primary-600 hover:text-primary-700">
          Back to doctors
        </Link>
      </div>
    );
  }

  const isPresent = (val) => val && val.toString().trim() !== '' && val.toString().trim().toUpperCase() !== 'N/A' && val.toString().trim() !== '—';

  const specUniv = doctor?.specializationUniversity || doctor?.user?.onboardingData?.specializationUniversity;
  const specYear = doctor?.specializationYear || doctor?.user?.onboardingData?.specializationYear;
  const hospAddr = doctor?.hospitalAddress || doctor?.user?.onboardingData?.hospitalAddress;
  const optExpertise = doctor?.optionalExpertise || doctor?.user?.onboardingData?.optionalExpertise;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            ←
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Doctor Details</h1>
        </div>
        <div className="flex space-x-2">
          {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') &&
            doctor.verificationStatus === 'pending' && (
              <button
                onClick={() => setShowVerifyModal(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Verify Doctor
              </button>
            )}
          {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
            <>
              <button
                onClick={handleToggleWebsiteVisibility}
                disabled={updatingVisibility}
                className={`px-3.5 py-2 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 border shadow-sm ${
                  doctor.showOnWebsite !== false
                    ? 'bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-300'
                    : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-300'
                } disabled:opacity-50`}
                title={doctor.showOnWebsite !== false ? 'Click to hide this doctor from public website' : 'Click to show this doctor on public website'}
              >
                {doctor.showOnWebsite !== false ? (
                  <>
                    <EyeOff className="w-4 h-4 text-amber-600" />
                    <span>Hide from Website</span>
                  </>
                ) : (
                  <>
                    <Globe className="w-4 h-4 text-emerald-600" />
                    <span>Show on Website</span>
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setEditModalTab('professional');
                  setShowEditModal(true);
                }}
                className="px-3.5 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 shadow-sm"
                title="Edit doctor profile (professional, personal, address, financials)"
              >
                <User className="w-4 h-4 text-slate-500" />
                <span>Edit Profile</span>
              </button>
              <button
                onClick={() => setShowWebsiteModal(true)}
                className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 shadow-sm"
                title="Customize public website details, dynamic fields, custom quick facts, and visibility"
              >
                <Globe className="w-4 h-4" />
                <span>Customize Website</span>
              </button>
              <button
                onClick={() => navigate(`/doctors/${id}/availability`)}
                className="px-4 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 font-semibold rounded-lg hover:bg-indigo-100 transition-all flex items-center gap-2"
              >
                Edit Roster
              </button>
              <button
                onClick={handleRequestAvailability}
                disabled={requestingAvailability}
                className="px-4 py-2 bg-[#143E78] text-white font-semibold rounded-lg hover:bg-[#1C56A3] disabled:opacity-50 flex items-center gap-2"
              >
                {requestingAvailability ? 'Sending Request...' : 'Request Update'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Professional Information</h2>
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                <button
                  onClick={() => {
                    setEditModalTab('professional');
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
                >
                  Edit Info
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Specialization (Degree)</p>
                <p className="font-medium">{doctor.specialization || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Qualification</p>
                <p className="font-medium">{doctor.primaryQualification || doctor.qualification || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Year of Completion</p>
                <p className="font-medium">{doctor.yearOfCompletion || doctor.user?.onboardingData?.yearOfCompletion || 'N/A'}</p>
              </div>
              {(doctor.specializationYear || doctor.user?.onboardingData?.specializationYear) && (
                <div>
                  <p className="text-sm text-gray-600">Specialization Year</p>
                  <p className="font-medium">{doctor.specializationYear || doctor.user?.onboardingData?.specializationYear}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600">Total Experience (Years)</p>
                <p className="font-medium">{doctor.totalExperience || doctor.user?.onboardingData?.totalExperience || 0} years</p>
              </div>
              {(doctor.postSpecializationExperience || doctor.user?.onboardingData?.postSpecializationExperience) ? (
                <div>
                  <p className="text-sm text-gray-600">Post-Specialization Exp.</p>
                  <p className="font-medium">{doctor.postSpecializationExperience || doctor.user?.onboardingData?.postSpecializationExperience} years</p>
                </div>
              ) : null}
              <div>
                <p className="text-sm text-gray-600">Registration Number</p>
                <p className="font-medium">{doctor.registrationNumber || doctor.user?.onboardingData?.registrationNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Council Name</p>
                <p className="font-medium">{doctor.councilName || doctor.user?.onboardingData?.councilName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Working Hospital</p>
                <p className="font-medium">{doctor.workingHospital || doctor.user?.onboardingData?.workingHospital || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Indemnity Insurance</p>
                <p className="font-medium">{(doctor.insurance?.hasInsurance || doctor.user?.onboardingData?.insurance?.hasInsurance) ? 'Yes' : 'No'}</p>
              </div>
            </div>
            {(doctor.workingHospital || doctor.hospitalAddress) && (
              <div className="mt-6 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Working Hospital/Clinic</p>
                  <p className="font-medium text-[#143E78]">{doctor.workingHospital || 'Not Specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Hospital/Clinic Address</p>
                  <p className="font-medium text-slate-800">{doctor.hospitalAddress || 'Not Specified'}</p>
                </div>
              </div>
            )}
            {doctor.bio && (
              <div className="mt-4">
                <p className="text-sm text-gray-600">Bio</p>
                <p className="font-medium">{doctor.bio}</p>
              </div>
            )}
          </div>


          {doctor.availableDays && doctor.availableDays.length > 0 && !availability && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Legacy Availability</h2>
              <div className="flex flex-wrap gap-2">
                {doctor.availableDays.map((day) => (
                  <span
                    key={day}
                    className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm"
                  >
                    {day}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* New Detailed Availability */}
          {availability && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-xl font-black text-[#143E78] uppercase tracking-tight">Availability Roster</h2>
              </div>



              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...availability.weeklySchedule]
                  .sort((a, b) => {
                    const dateA = getDayDateObj(a.day) || new Date(8640000000000000);
                    const dateB = getDayDateObj(b.day) || new Date(8640000000000000);
                    return dateA - dateB;
                  })
                  .map((day) => (
                    <div
                      key={day.day}
                      className={`p-4 rounded-2xl border-2 transition-all ${day.isAvailable
                        ? 'border-blue-100 bg-blue-50/20'
                        : 'border-gray-50 bg-gray-50/50 opacity-60'
                        }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex flex-col">
                          <span className={`font-black uppercase tracking-tighter ${day.isAvailable ? 'text-[#143E78]' : 'text-gray-400'}`}>
                            {day.day}
                          </span>
                          {getDayDate(day.day) && (
                            <span className="text-[10px] font-bold text-gray-400 mt-0.5">
                              {getDayDate(day.day)}
                            </span>
                          )}
                        </div>
                        {day.isAvailable && <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>}
                      </div>

                      {day.isAvailable && day.timeSlots?.length > 0 ? (
                        <div className="space-y-2">
                          {day.timeSlots.map((slot, idx) => (
                            <div key={idx} className="flex flex-wrap gap-2 text-[11px]">
                              <span className="bg-white px-2 py-1 rounded-md border border-blue-100 font-bold text-[#143E78]">
                                {slot.startTime} - {slot.endTime}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[10px] font-bold text-gray-400 italic">Not Available</span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {doctor.languages && doctor.languages.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Languages</h2>
              <div className="flex flex-wrap gap-2">
                {doctor.languages.map((language) => (
                  <span
                    key={language}
                    className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm"
                  >
                    {language}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Surgeries Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-black text-[#143E78] uppercase tracking-tight mb-6">Upcoming Surgeries</h2>
            {upcomingSurgeries.length > 0 ? (
              <div className="space-y-4 mb-8">
                {upcomingSurgeries.map(surgery => (
                  <Link key={surgery._id} to={`/surgeries/${surgery._id}`} className="flex justify-between items-center p-4 bg-blue-50 border border-blue-100 rounded-xl hover:bg-blue-100 transition-colors">
                    <div>
                      <p className="font-bold text-[#143E78]">{surgery.surgeryName} <span className="text-xs font-normal text-gray-500">({surgery.surgeryType})</span></p>
                      <p className="text-xs text-gray-600 mt-1">Patient: {surgery.lead?.patientName || 'N/A'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-800">{new Date(surgery.scheduledDate).toLocaleDateString('en-GB')}</p>
                      <p className="text-xs text-gray-500">{surgery.scheduledTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic mb-8">No upcoming surgeries scheduled.</p>
            )}

            <h2 className="text-xl font-black text-[#143E78] uppercase tracking-tight mb-6">Previous Surgeries</h2>
            {previousSurgeries.length > 0 ? (
              <div className="space-y-4">
                {previousSurgeries.map(surgery => (
                  <Link key={surgery._id} to={`/surgeries/${surgery._id}`} className="flex justify-between items-center p-4 bg-gray-50 border border-gray-100 rounded-xl hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="font-bold text-gray-700">{surgery.surgeryName}</p>
                      <p className="text-xs text-gray-500 mt-1">Status: <span className="uppercase">{surgery.status.replace(/_/g, ' ')}</span></p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-600">{new Date(surgery.scheduledDate).toLocaleDateString('en-GB')}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No previous surgeries recorded.</p>
            )}
          </div>

          {/* Public Website Profile & Dynamic Visibility Card */}
          <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-sm space-y-6">
            <div className="flex justify-between items-center border-b border-slate-100 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-[#143E78] uppercase tracking-tight">Website Profile &amp; Dynamic Data</h2>
                  <p className="text-xs text-slate-400">Controls data rendered on doxez.in/doctors for this doctor</p>
                </div>
              </div>
              <button
                onClick={() => setShowWebsiteModal(true)}
                className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-all shadow-sm flex items-center gap-1.5"
              >
                <span>Customize Website Page</span>
              </button>
            </div>

            {/* Quick status row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Public Status</p>
                <p className={`text-xs font-bold mt-1 ${doctor.showOnWebsite !== false ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {doctor.showOnWebsite !== false ? '● Live on Website' : '○ Hidden from Website'}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Availability Badge</p>
                <p className="text-xs font-bold text-slate-800 mt-1 truncate">
                  {doctor.websiteProfile?.availabilityBadge || 'Available for Consultation'}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Consultation Type</p>
                <p className="text-xs font-bold text-slate-800 mt-1 truncate">
                  {doctor.websiteProfile?.consultationType || 'In-person / Tele-Consultation'}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Specializations</p>
                <p className="text-xs font-bold text-indigo-600 mt-1">
                  {(doctor.websiteProfile?.specializations?.length || 0)} Pills Added
                </p>
              </div>
            </div>

            {/* Section Visibility Toggles overview */}
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3">
              <p className="text-[10px] font-black text-[#143E78] uppercase tracking-widest">Website Section Visibility Status</p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className={`px-2.5 py-1 rounded-lg font-bold border ${doctor.websiteProfile?.showAbout !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                  About: {doctor.websiteProfile?.showAbout !== false ? 'Visible' : 'Hidden'}
                </span>
                <span className={`px-2.5 py-1 rounded-lg font-bold border ${doctor.websiteProfile?.showQuickFacts !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                  Quick Facts: {doctor.websiteProfile?.showQuickFacts !== false ? 'Visible' : 'Hidden'}
                </span>
                <span className={`px-2.5 py-1 rounded-lg font-bold border ${doctor.websiteProfile?.showSpecializations !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                  Specializations: {doctor.websiteProfile?.showSpecializations !== false ? 'Visible' : 'Hidden'}
                </span>
                <span className={`px-2.5 py-1 rounded-lg font-bold border ${doctor.websiteProfile?.showEducation !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                  Education: {doctor.websiteProfile?.showEducation !== false ? 'Visible' : 'Hidden'}
                </span>
                <span className={`px-2.5 py-1 rounded-lg font-bold border ${doctor.websiteProfile?.showAreasOfExpertise !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                  Expertise: {doctor.websiteProfile?.showAreasOfExpertise !== false ? 'Visible' : 'Hidden'}
                </span>
                <span className={`px-2.5 py-1 rounded-lg font-bold border ${doctor.websiteProfile?.showProcedures !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                  Procedures: {doctor.websiteProfile?.showProcedures !== false ? 'Visible' : 'Hidden'}
                </span>
                <span className={`px-2.5 py-1 rounded-lg font-bold border ${doctor.websiteProfile?.showFaqs !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                  FAQs: {doctor.websiteProfile?.showFaqs !== false ? 'Visible' : 'Hidden'}
                </span>
                <span className={`px-2.5 py-1 rounded-lg font-bold border ${doctor.websiteProfile?.showRelatedDoctors !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                  Related Doctors: {doctor.websiteProfile?.showRelatedDoctors !== false ? 'Visible' : 'Hidden'}
                </span>
              </div>

              {/* Hero visibility badges */}
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest pt-1">Hero Header Elements</p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className={`px-2 py-0.5 rounded-md font-semibold text-[11px] border ${doctor.websiteProfile?.showVerifiedBadge !== false ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                  Verified Badge: {doctor.websiteProfile?.showVerifiedBadge !== false ? 'ON' : 'OFF'}
                </span>
                <span className={`px-2 py-0.5 rounded-md font-semibold text-[11px] border ${doctor.websiteProfile?.showAvailabilityBadge !== false ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                  Status Pill: {doctor.websiteProfile?.showAvailabilityBadge !== false ? 'ON' : 'OFF'}
                </span>
                <span className={`px-2 py-0.5 rounded-md font-semibold text-[11px] border ${doctor.websiteProfile?.showQualificationsSummary !== false ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                  Qualifications: {doctor.websiteProfile?.showQualificationsSummary !== false ? 'ON' : 'OFF'}
                </span>
                <span className={`px-2 py-0.5 rounded-md font-semibold text-[11px] border ${doctor.websiteProfile?.showDesignation !== false ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                  Specialization: {doctor.websiteProfile?.showDesignation !== false ? 'ON' : 'OFF'}
                </span>
                <span className={`px-2 py-0.5 rounded-md font-semibold text-[11px] border ${doctor.websiteProfile?.showExperience !== false ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                  Experience Box: {doctor.websiteProfile?.showExperience !== false ? 'ON' : 'OFF'}
                </span>
                <span className={`px-2 py-0.5 rounded-md font-semibold text-[11px] border ${doctor.websiteProfile?.showLocation !== false ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                  Location Box: {doctor.websiteProfile?.showLocation !== false ? 'ON' : 'OFF'}
                </span>
                <span className={`px-2 py-0.5 rounded-md font-semibold text-[11px] border ${doctor.websiteProfile?.showBookAppointment !== false ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                  Book Appointment Button: {doctor.websiteProfile?.showBookAppointment !== false ? 'ON' : 'OFF'}
                </span>
              </div>
            </div>

            {/* Specialization pills preview */}
            {doctor.websiteProfile?.specializations?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs text-gray-500 font-semibold">Specialization Pills</p>
                <div className="flex flex-wrap gap-1.5">
                  {doctor.websiteProfile.specializations.map((spec, i) => (
                    <span key={i} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-lg text-xs font-semibold">
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Education preview */}
            {doctor.websiteProfile?.education?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-semibold">Education &amp; Qualifications ({doctor.websiteProfile.education.length} entries)</p>
                <div className="space-y-1.5">
                  {doctor.websiteProfile.education.map((edu, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs bg-slate-50 p-2.5 rounded-xl">
                      <span className="font-bold text-indigo-600 w-16">{edu.year || '—'}</span>
                      <span className="font-bold text-slate-800">{edu.degree}</span>
                      <span className="text-slate-400">at {edu.institution}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Procedures preview */}
            {doctor.websiteProfile?.procedures?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs text-gray-500 font-semibold">Procedures Performed ({doctor.websiteProfile.procedures.length})</p>
                <div className="flex flex-wrap gap-2">
                  {doctor.websiteProfile.procedures.map((p, i) => (
                    <span key={i} className="px-3 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-bold">
                      {p.title}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                <button
                  onClick={() => {
                    setEditModalTab('personal');
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
                >
                  Edit Personal
                </button>
              )}
            </div>
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-blue-50 overflow-hidden border-2 border-blue-100 shrink-0 flex items-center justify-center">
                {(() => {
                  const avatarUrl = doctor.user?.avatar || doctor.documents?.find(d => d.type === 'PHOTO')?.url;
                  const initials = (
                    <div className="w-full h-full flex items-center justify-center bg-blue-50 text-blue-500 font-black text-xl">
                      {doctor.user?.firstName?.[0] || ''}{doctor.user?.lastName?.[0] || ''}
                    </div>
                  );
                  if (avatarUrl) {
                    return (
                      <div className="relative w-full h-full">
                        <img
                          src={getAvatarUrl(avatarUrl)}
                          alt="Profile"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = e.target.parentNode.querySelector('.avatar-fallback');
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="avatar-fallback absolute inset-0 hidden items-center justify-center bg-blue-50 text-blue-500 font-black text-xl">
                          {doctor.user?.firstName?.[0] || ''}{doctor.user?.lastName?.[0] || ''}
                        </div>
                      </div>
                    );
                  }
                  return initials;
                })()}
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Doctor</p>
                <p className="font-bold text-lg text-slate-800 leading-tight">
                  Dr. {doctor.user?.firstName} {doctor.user?.lastName}
                </p>
              </div>
            </div>
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Email</p>
                <p className="font-medium text-slate-700">{doctor.user?.email}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Phone</p>
                <p className="font-medium text-slate-700">{doctor.user?.phone}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Alternative Phone</p>
                <p className="font-medium text-slate-700">{doctor.user?.alternativePhone || doctor.user?.onboardingData?.alternativePhone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Emergency Contact</p>
                <p className="font-medium text-slate-700">
                  {(() => {
                    const person = doctor.user?.onboardingData?.emergencyContact || doctor.onboardingData?.emergencyContact || doctor.emergencyContact;
                    const phoneNum = doctor.user?.onboardingData?.emergencyContactNumber || doctor.user?.onboardingData?.emergencyContactPersonNumber || doctor.onboardingData?.emergencyContactNumber || doctor.onboardingData?.emergencyContactPersonNumber || doctor.emergencyContactNumber;
                    if (!person && !phoneNum) return 'N/A';
                    return (
                      <>
                        <span>{person || 'Emergency Contact'}</span>
                        {phoneNum && (
                          <span className="ml-1 text-[#143E78] font-bold">
                            ({phoneNum})
                          </span>
                        )}
                      </>
                    );
                  })()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Gender Profile</p>
                <p className="font-bold text-slate-700">{doctor.user?.gender || doctor.user?.onboardingData?.gender || 'Not Specified'}</p>
              </div>
            </div>
          </div>

          {/* Geographic Profile */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-indigo-500" />
                Geographic Profile
              </h2>
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                <button
                  onClick={() => {
                    setEditModalTab('address');
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
                >
                  Edit Address
                </button>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Street Address</p>
                <p className="font-medium text-slate-700">{doctor.user?.address?.street || doctor.user?.onboardingData?.currentAddress || 'No street details'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">City</p>
                  <p className="font-medium text-slate-700">{doctor.user?.address?.city || doctor.user?.onboardingData?.city || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">State</p>
                  <p className="font-medium text-slate-700">{doctor.user?.address?.state || doctor.user?.onboardingData?.state || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">Pincode</p>
                  <p className="font-medium text-slate-700">{doctor.user?.address?.pincode || doctor.user?.onboardingData?.pincode || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">Country</p>
                  <p className="font-medium text-slate-700">{doctor.user?.address?.country || 'India'}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Gender</p>
                <p className="font-medium">{doctor.user?.gender || 'Not Specified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date of Birth</p>
                <p className="font-medium">
                  {doctor.user?.dob ? new Date(doctor.user.dob).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not Specified'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Residential Address</p>
                <p className="font-medium text-slate-800">
                  {doctor.user?.address?.street ? (
                    `${doctor.user.address.street}, ${doctor.user.address.city || ''}, ${doctor.user.address.state || ''} - ${doctor.user.address.pincode || ''}`
                  ) : 'Not Specified'}
                </p>
              </div>
            </div>
          </div>

          {/* Website Visibility Control Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                Website Visibility
              </h2>
              <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full border ${
                doctor.showOnWebsite !== false 
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                {doctor.showOnWebsite !== false ? '● Visible on Web' : '○ Hidden from Web'}
              </span>
            </div>
            
            <p className="text-xs text-gray-500 mb-4 leading-relaxed">
              {doctor.showOnWebsite !== false 
                ? 'This doctor profile is active and shown on the public website service pages matching their specialization and branch.' 
                : 'This doctor profile is currently hidden from public website service pages. Only visible to internal staff in CRM.'}
            </p>

            {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
              <button
                onClick={handleToggleWebsiteVisibility}
                disabled={updatingVisibility}
                className={`w-full py-2.5 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-sm ${
                  doctor.showOnWebsite !== false
                    ? 'bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300'
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                } disabled:opacity-50`}
              >
                {updatingVisibility ? (
                  'Updating...'
                ) : doctor.showOnWebsite !== false ? (
                  <>
                    <EyeOff className="w-4 h-4" />
                    Hide from Website
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4" />
                    Show on Website
                  </>
                )}
              </button>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Verification Status</h2>
            <span className={`px-3 py-1 text-sm rounded-full ${getVerificationColor(doctor.verificationStatus)}`}>
              {doctor.verificationStatus}
            </span>
            {doctor.verifiedBy && (
              <div className="mt-4">
                <p className="text-sm text-gray-600">CC (Verified By)</p>
                <p className="font-medium text-indigo-600">{doctor.verifiedBy.firstName} {doctor.verifiedBy.lastName}</p>
              </div>
            )}
            {doctor.user?.invitedBy && (
              <div className="mt-4">
                <p className="text-sm text-gray-600">CC (Invited By)</p>
                <p className="font-medium text-slate-600">{doctor.user.invitedBy.firstName} {doctor.user.invitedBy.lastName}</p>
              </div>
            )}
            {doctor.rejectionReason && (
              <div className="mt-4">
                <p className="text-sm text-gray-600">Rejection Reason</p>
                <p className="font-medium text-red-600">{doctor.rejectionReason}</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h2>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Total Surgeries</p>
                <p className="font-medium">{surgeries.length}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Earnings</p>
                <p className="font-medium">
                  ₹{doctor.totalEarnings?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </div>

          {doctor.hospitals && doctor.hospitals.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Affiliated Hospitals</h2>
              <div className="space-y-2">
                {doctor.hospitals.map((h) => (
                  <div key={h.hospital?._id} className="flex items-center justify-between">
                    <p className="font-medium">{h.hospital?.hospitalName}</p>
                    {h.isPrimary && (
                      <span className="px-2 py-1 text-xs bg-primary-100 text-primary-800 rounded">
                        Primary
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detailed Professional & Qualification Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Detailed Credentials</h2>
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                <button
                  onClick={() => {
                    setEditModalTab('professional');
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
                >
                  Edit Credentials
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-y-4 gap-x-8">
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Doctor Type</p>
                <p className="font-bold text-[#143E78]">{doctor.doctorType || doctor.user?.onboardingData?.doctorType || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Registration Number</p>
                <p className="font-bold text-[#143E78]">{doctor.registrationNumber || doctor.user?.onboardingData?.registrationNumber || 'N/A'}</p>
              </div>

              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Primary Qualification</p>
                <p className="font-bold text-[#143E78]">{doctor.primaryQualification || doctor.qualification || doctor.user?.onboardingData?.primaryQualification || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Specialization</p>
                <p className="font-bold text-[#143E78]">{doctor.specialization || doctor.user?.onboardingData?.specialization || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Specialization Branch</p>
                <p className="font-bold text-[#143E78]">{doctor.specializationBranch || doctor.user?.onboardingData?.specializationBranch || 'N/A'}</p>
              </div>
              {isPresent(specUniv) && (
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">Specialization University</p>
                  <p className="font-bold text-[#143E78]">{specUniv}</p>
                </div>
              )}
              {isPresent(specYear) && (
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">Specialization Year</p>
                  <p className="font-bold text-[#143E78]">{specYear}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">University</p>
                <p className="font-bold text-[#143E78]">{doctor.universityName || doctor.user?.onboardingData?.universityName || 'N/A'} {doctor.yearOfCompletion || doctor.user?.onboardingData?.yearOfCompletion ? `(${doctor.yearOfCompletion || doctor.user?.onboardingData?.yearOfCompletion})` : ''}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Council Name</p>
                <p className="font-bold text-[#143E78]">{doctor.councilName || doctor.user?.onboardingData?.councilName || 'N/A'}</p>
              </div>

              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Total Experience</p>
                <p className="font-bold text-[#143E78]">{doctor.totalExperience ? `${doctor.totalExperience} Years` : (doctor.user?.onboardingData?.totalExperience ? `${doctor.user.onboardingData.totalExperience} Years` : 'N/A')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Post Spec. Experience</p>
                <p className="font-bold text-[#143E78]">{doctor.postSpecializationExperience ? `${doctor.postSpecializationExperience} Years` : (doctor.user?.onboardingData?.postSpecializationExperience ? `${doctor.user.onboardingData.postSpecializationExperience} Years` : 'N/A')}</p>
              </div>
              {isPresent(hospAddr) && (
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">Hospital Address</p>
                  <p className="font-bold text-[#143E78]">{hospAddr}</p>
                </div>
              )}
              {isPresent(optExpertise) && (
                <div className="col-span-2">
                  <p className="text-xs text-gray-400 uppercase font-black">Optional Expertise</p>
                  <p className="font-bold text-[#143E78]">{optExpertise}</p>
                </div>
              )}
            </div>

            {/* Emergency On Call Settings */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Emergency On Call</p>
                <p className="font-bold text-[#143E78]">{doctor.emergencyOnCall?.isAvailable ? `Yes (${doctor.emergencyOnCall.scheduleNote})` : 'No'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-y-4 gap-x-8 mt-4 pt-4 border-t border-gray-100">
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">PAN Number</p>
                <p className="font-bold text-[#143E78]">{doctor.financials?.panNumber || doctor.user?.onboardingData?.panNumber || doctor.user?.panNumber || doctor.panNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Aadhaar Number</p>
                <p className="font-bold text-[#143E78]">{doctor.financials?.aadhaarNumber || doctor.user?.onboardingData?.aadhaarNumber || doctor.user?.aadhaarNumber || doctor.aadhaarNumber || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="bg-white rounded-lg shadow p-6 border-t-4 border-green-500">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Payout & Banking Details</h2>
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                <button
                  onClick={() => {
                    setEditModalTab('financial');
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
                >
                  Edit Banking
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Bank Name</p>
                <p className="font-bold">{doctor.financials?.bankName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Account Number</p>
                <p className="font-bold">{doctor.financials?.accountNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">IFSC Code</p>
                <p className="font-bold">{doctor.financials?.ifscCode || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">PAN Number</p>
                <p className="font-bold">{doctor.financials?.panNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Aadhaar Number</p>
                <p className="font-bold">{doctor.financials?.aadhaarNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">UPI ID</p>
                <p className="font-bold text-blue-600">{doctor.financials?.upiId || 'N/A'}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">Consultation Fee</p>
                  <p className="text-xl font-black text-[#143E78]">₹{doctor.financials?.consultationFee?.toLocaleString() || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">Expected Charges/Case</p>
                  <p className="text-xl font-black text-green-700">₹{doctor.financials?.expectedChargesPerCase?.toLocaleString() || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Uploaded Documents */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Verification Documents</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {doctor.documents && doctor.documents.length > 0 ? (
                doctor.documents.map((doc, idx) => {
                  const isPdf = doc.url?.toLowerCase().includes('.pdf') ||
                    doc.url?.toLowerCase().includes('/raw/') ||
                    doc.type?.toUpperCase().includes('PDF');
                  const displayUrl = getDocumentUrl(doc.url);
                  return (
                    <div key={idx} className="flex flex-col border border-gray-100 rounded-2xl overflow-hidden bg-gray-50/30 group">
                      {isPdf ? (
                        // PDF — show a styled placeholder with a direct open link
                        <a
                          href={displayUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-32 bg-blue-50 flex flex-col items-center justify-center text-blue-400 hover:bg-blue-100 transition-colors gap-2"
                        >
                          <ClipboardCheck size={40} />
                          <span className="text-[10px] font-black uppercase tracking-widest text-blue-400">Open PDF</span>
                        </a>
                      ) : (
                        // Image — routed through proxy, with onError fallback
                        <div className="h-32 bg-gray-100 relative overflow-hidden">
                          <img
                            src={displayUrl}
                            alt={doc.type}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.parentNode.querySelector('.doc-fallback');
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                          <div
                            className="doc-fallback absolute inset-0 hidden items-center justify-center flex-col gap-1 bg-blue-50 text-blue-300"
                          >
                            <ClipboardCheck size={40} />
                            <span className="text-[9px] font-black uppercase tracking-widest text-blue-300">Open File</span>
                          </div>
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors pointer-events-none" />
                        </div>
                      )}

                      <div className="p-4">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{doc.type?.replace(/_/g, ' ')}</p>
                        <a
                          href={displayUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-bold text-[#143E78] hover:text-blue-600 flex items-center gap-2"
                        >
                          View Full Document ↗
                        </a>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full py-10 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-100">
                  <p className="text-sm text-gray-400 font-bold uppercase tracking-widest">No documents uploaded by doctor</p>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Verify Modal */}
      {showVerifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Verify Doctor</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={verificationStatus}
                  onChange={(e) => setVerificationStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              {verificationStatus === 'rejected' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Reason</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowVerifyModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleVerify}
                disabled={updating}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {updating ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <DoctorEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        doctor={doctor}
        onSuccess={fetchDoctor}
        defaultTab={editModalTab}
      />

      {/* Dedicated Website Profile Customizer Modal */}
      <DoctorWebsiteModal
        isOpen={showWebsiteModal}
        onClose={() => setShowWebsiteModal(false)}
        doctor={doctor}
        onSuccess={fetchDoctor}
      />
    </div>
  );
};

export default DoctorDetail;
